import { cn } from "@tcg/ui";
import { X } from "lucide-react";
import { useEffect, useId, useRef, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

export interface ModalProps {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  /** One sentence under the title (why this is asked — design/04 §0). */
  description?: string;
  footer?: ReactNode;
  size?: "md" | "lg";
  /** The close (×) button / Esc / backdrop are off while something is being sent. */
  busy?: boolean;
}

/** Centered dialog for the register (focus moves in, Esc closes, background inert). */
export function Modal({
  open,
  title,
  onClose,
  children,
  description,
  footer,
  size = "md",
  busy = false,
}: ModalProps) {
  const { t } = useTranslation();
  const titleId = useId();
  const descId = useId();
  const panel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    const previous = document.activeElement as HTMLElement | null;
    const first = panel.current?.querySelector<HTMLElement>(
      "input, select, textarea, button:not([data-close])",
    );
    (first ?? panel.current)?.focus();
    return () => previous?.focus?.();
  }, [open]);

  if (!open) {
    return null;
  }
  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) {
          onClose();
        }
      }}
    >
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descId : undefined}
        tabIndex={-1}
        onKeyDown={(e) => {
          if (e.key === "Escape" && !busy) {
            e.stopPropagation();
            onClose();
          }
        }}
        className={cn(
          "flex max-h-[90vh] w-full flex-col overflow-hidden rounded-lg border bg-background shadow-xl outline-none",
          size === "lg" ? "max-w-3xl" : "max-w-lg",
        )}
      >
        <header className="flex items-start gap-3 border-b px-5 py-4">
          <div className="flex-1">
            <h2 id={titleId} className="text-xl font-semibold">
              {title}
            </h2>
            {description ? (
              <p id={descId} className="mt-1 text-sm text-muted-foreground">
                {description}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            data-close
            onClick={onClose}
            disabled={busy}
            aria-label={t("app.close")}
            className="flex size-pos-touch items-center justify-center rounded-md hover:bg-accent disabled:opacity-50"
          >
            <X aria-hidden className="size-6" />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
        {footer ? (
          <footer className="flex flex-wrap justify-end gap-2 border-t px-5 py-3">{footer}</footer>
        ) : null}
      </div>
    </div>
  );
}
